import { Injectable } from "@angular/core";
import { RegionFacilityRegistry, FacilityEntry, FacilitiySettings, FacilitiesService, EhrType } from 'list-types-public';
import { ServerTokenService } from "../services/server-token.service";
import { CustomAuthServiceService } from "../auth/custom-auth-service.service";

/**
 * FacilityService - Modern facility management service
 *
 * This service provides ID-based facility operations using the RegionFacilityRegistry.
 * For new code, use this service instead of the legacy name-based methods in UtilsService.
 *
 * @example
 * ```typescript
 * constructor(private facilityService: FacilityService) {}
 *
 * ngOnInit() {
 *   const facilities = this.facilityService.getUserFacilities();
 *   const displayName = this.facilityService.getFacilityDisplayName(facilityId);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class FacilityService {

  /** RegionFacilityRegistry instance - null until data loads from server */
  regionFacilityRegistry: RegionFacilityRegistry | null = null;

  /** User's accessible facility IDs */
  userFacilitiesIds: number[] = [];

  /** True if user has access to all facilities */
  userAllFacilities = false;

  ehrNames: string[] = [];

  constructor(
    private server: ServerTokenService,
    private auth: CustomAuthServiceService
  ) {
    console.log('FacilityService constructor');

    // Load facility data when user authenticates
    auth.authState$.subscribe((state) => {
      if (state) {
        this.loadFacilityData();
        this.loadUserPermissions();
      }
    });
  }

  /**
   * Load facility data from server and initialize RegionFacilityRegistry
   * @private
   */
  private loadFacilityData(): void {
    this.server.postWithToken('snfs/getFacilitiesSettings', {}).subscribe((res: any) => {
      this.updateSettings(res);
    });
  }

  /**
   * Load user's facility permissions
   * @private
   */
  private loadUserPermissions(): void {
    const userSettings = this.auth.currentUserValue()?.settings;
    if (userSettings?.allFacilities) {
      this.userAllFacilities = true;
    } else {
      this.userFacilitiesIds = userSettings?.facilities || [];
    }
  }

  /**
   * Update facility settings and build RegionFacilityRegistry
   * @private
   */
  private updateSettings(settingsJsonObject: FacilitiySettings): void {
    // Initialize legacy FacilitiesService for backward compatibility
    const legacySettings = new FacilitiesService(settingsJsonObject);

    const regionIdStr = this.auth.currentUserValue()?.regionId;
    const regionId = regionIdStr != null ? Number(regionIdStr) : undefined;

    // Build RegionFacilityRegistry from legacy data
    const pccImports = Object.entries(legacySettings.sites).map(([pccName, facilityId]) => ({
      fac_id: facilityId,
      name: pccName,
      regionId
    }));

    this.ehrNames = Object.keys(settingsJsonObject.EHR.names)
    .filter(name => ![EhrType.Manual, EhrType.Efax].includes(name as EhrType))
    .sort((a, b) => a.localeCompare(b));
  if (settingsJsonObject.EHR.names[EhrType.Efax]) {
    this.ehrNames.push(EhrType.Efax);
  }
  if (settingsJsonObject.EHR.names[EhrType.Manual]) {
    this.ehrNames.push(EhrType.Manual);
  }


    const facilitySettings = Object.entries(legacySettings.sites).map(([pccName, facilityId]) => ({
      facilityId: facilityId,
      displayName: legacySettings.displayNames?.[facilityId] || pccName,
      ehrNames: legacySettings.getFacilityEHRsNames(pccName) || {},
      ownerActive: true,
      referralActive: Object.keys(legacySettings.facilities?.names || {}).includes(pccName),
      reportsActive: true,
      assistedLiving: false,
      regionId
    }));
    this.regionFacilityRegistry = new RegionFacilityRegistry(pccImports, facilitySettings, regionId);
  }

  /**
   * Refresh facility data from server
   * Useful when facility data might have changed
   */
  async refreshSettings(): Promise<void> {
    console.log('FacilityService: refreshSettings start');
    const facilitiesSettings = await this.server.postWithToken('snfs/getFacilitiesSettings', {}).toPromise();
    this.updateSettings(facilitiesSettings as FacilitiySettings);
    this.loadUserPermissions();
  }

  // ==================== PUBLIC API - ID-BASED METHODS ====================
  // Use these methods for all new code

  /**
   * Get display name for a facility (for UI)
   * @param facilityId - Facility ID
   * @returns Display name string
   * @example
   * const name = facilityService.getFacilityDisplayName(12); // "Atlanta Care Center"
   */
  getFacilityDisplayName(facilityId: number): string {
    return this.regionFacilityRegistry?.getDisplayName(facilityId) || '';
  }

  /**
   * Get PCC name for a facility (for reports/internal use)
   * @param facilityId - Facility ID
   * @returns PCC name string
   * @example
   * const pccName = facilityService.getFacilityPccName(12); // "Atlanta Care"
   */
  getFacilityPccName(facilityId: number): string {
    return this.regionFacilityRegistry?.getPccName(facilityId) || '';
  }

  /**
   * Check if facility exists
   * @param facilityId - Facility ID
   * @returns true if exists
   * @example
   * if (facilityService.facilityExists(12)) { ... }
   */
  facilityExists(facilityId: number): boolean {
    return this.regionFacilityRegistry?.exists(facilityId) || false;
  }

  /**
   * Get facilities accessible to current user
   * @returns Array of FacilityEntry objects with full facility details
   * @example
   * const facilities = facilityService.getUserFacilities();
   * facilities.forEach(f => console.log(f.id, f.displayName, f.pccName));
   */
  getUserFacilities(): FacilityEntry[] {
    if (!this.regionFacilityRegistry) return [];

    if (this.userAllFacilities) {
      return this.regionFacilityRegistry.getAccessible('all', false);
    } else if (this.userFacilitiesIds?.length) {
      return this.regionFacilityRegistry.getAccessible(this.userFacilitiesIds, false);
    }
    return [];
  }

  /**
   * Get facility IDs accessible to current user
   * @returns Array of facility IDs
   * @example
   * const ids = facilityService.getUserFacilityIds(); // [12, 14, 16]
   */
  getUserFacilityIds(): number[] {
    if (this.userAllFacilities) {
      return this.regionFacilityRegistry?.getAllIds() || [];
    }
    return this.userFacilitiesIds || [];
  }

  /**
   * Get all facility IDs in the system
   * @returns Array of all facility IDs
   */
  getAllFacilityIds(): number[] {
    return this.regionFacilityRegistry?.getAllIds() || [];
  }

  /**
   * Get facility entry by numeric ID
   * @returns FacilityEntry or undefined
   */
  getFacilityEntry(id: number): FacilityEntry | undefined {
    return this.regionFacilityRegistry?.getById(id);
  }

  /**
   * Get EHR-specific name for a facility
   * @param facilityId - Facility ID
   * @param ehrName - EHR system name
   * @returns EHR-specific facility name or empty string
   * @example
   * const epicName = facilityService.getEhrName(12, "Epic"); // "Epic Atlanta"
   */
  getEhrName(facilityId: number, ehrName: string): string {
    const facility = this.regionFacilityRegistry?.getById(facilityId);
    return facility?.ehrNames?.[ehrName] || '';
  }

  /**
   * Check if facility registry is loaded and ready
   * @returns true if registry is loaded
   */
  isReady(): boolean {
    return this.regionFacilityRegistry !== null;
  }

  /** All ownerActive facilities map — loaded on demand for admin pages */
  private allFacilitiesMap: Record<number, string> = {};

  /**
   * Load all ownerActive facilities from server (not filtered by user).
   * Call this on admin pages that need to display names for any facility ID.
   */
  async loadAllFacilities(): Promise<void> {
    const data = await this.server.postWithToken<{ facilityId: number, name: string, displayName: string }[]>(
      'snfs/getAllFacilities', {}
    ).toPromise();
    this.allFacilitiesMap = {};
    data?.forEach(f => {
      this.allFacilitiesMap[f.facilityId] = f.name;
    });
  }

  /**
   * Get PCC name for any facility ID, including those not accessible to the current user.
   * Requires loadAllFacilities() to have been called first.
   */
  getPccNameFromAll(facilityId: number): string {
    return this.allFacilitiesMap[facilityId] || '';
  }

  /**
   * Wait until facility registry is loaded and ready
   * @returns Promise that resolves when facilities are loaded
   * @example
   * async ngOnInit() {
   *   await this.facilityService.waitUntilReady();
   *   this.facilities = this.facilityService.getUserFacilities();
   * }
   */
  async waitUntilReady(): Promise<void> {
    if (this.regionFacilityRegistry) {
      return Promise.resolve();
    }
    // Poll until ready
    return new Promise((resolve) => {
      const checkReady = () => {
        if (this.regionFacilityRegistry) {
          resolve();
        } else {
          setTimeout(checkReady, 50);
        }
      };
      checkReady();
    });
  }

}
